/**
 * The Stock page's out-of-stock flags and the popup each opens (owner request, 2026-10-26: no
 * separate settings screen — the flags are on the list, and stock is set from where it is seen).
 *
 * vendor/isomorphic/apps/isomorphic/src/app/shared/modal-views/container.tsx
 *   the rizzui `Modal` and its dark-mode overlay, as `components/orders/complete-progress.tsx`;
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx
 *   a setting's explanation beside its control, as `pages/orders/Settings.tsx`.
 *
 * - `StockFlag` — a separate "Add stock" tag beside each row's availability badge. It opens the SKU's popup:
 *   its three figures, an In Stock checkbox and a Quantity (10,000 until changed).
 * - `LowStockFlag` — one flag in the page header: how many SKUs are going out of stock. It opens the
 *   warning's switch and kilogram threshold, and the list Warehouse reports (`GET /stock/low`), each
 *   opening the same In Stock / Quantity form.
 *
 * Saving adds the quantity to what is already on hand (`POST /adjustments`, a `count_correction` of
 * exactly that quantity). Unticking In Stock sets on hand to zero instead
 * (`POST /adjustments/set-on-hand`, where Warehouse works out the difference). Either way the change
 * is a movement with its reason in the history.
 */
import { useState, type FormEvent, type ReactNode } from "react";
import toast from "react-hot-toast";
import { Link, useOutletContext, useRevalidator } from "react-router-dom";
import { PiArrowLeftBold as BackIcon, PiPlusBold as PlusIcon, PiWarningDuotone as WarningIcon } from "react-icons/pi";
import { Badge, Button, Checkbox, Modal, Switch, Text, Title } from "rizzui";
import { ErrorSurface, Field, cn } from "@steelyard/theme";
import {
  createAdjustment,
  formatQty,
  setOnHand,
  setStockWarningSettings,
  type FreeStockView,
  type LowStockView,
} from "../../lib/services/warehouse";
import { inventoryPath } from "../../lib/paths";
import { can } from "../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../types";
import { AvailabilityBadge, type InventoryRow } from "./columns";

/** What the popup's Quantity starts at (owner request, 2026-10-26). */
const DEFAULT_QUANTITY = "10000";

type Sku = { skuId: string; name: string; skuCode: string; unit: string };

function useMaySetStock() {
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  return can(permissionState, "inventory.administer");
}

function Popup({ open, onClose, children }: { open: boolean; onClose: () => void; children: ReactNode }) {
  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      size="md"
      overlayClassName="backdrop-blur-sm dark:bg-opacity-40 dark:backdrop-blur-lg"
      containerClassName="!rounded-2xl !p-0"
    >
      {open ? children : null}
    </Modal>
  );
}

/**
 * In Stock and Quantity. Adds the quantity to on hand (or sets it to zero, unticked) and calls
 * `onSaved` once Warehouse has recorded it.
 */
function SetStockForm({ sku, onSaved, onCancel }: { sku: Sku; onSaved: () => void; onCancel: () => void }) {
  const [inStock, setInStock] = useState(true);
  const [quantity, setQuantity] = useState(DEFAULT_QUANTITY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function save(event: FormEvent) {
    event.preventDefault();
    const amount = Number(quantity.trim());
    if (inStock && (quantity.trim() === "" || !Number.isFinite(amount) || amount <= 0)) {
      toast.error("Enter a quantity above zero");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (inStock) {
        await createAdjustment({
          skuId: sku.skuId,
          qtyDelta: amount,
          reasonCode: "count_correction",
          note: `Added ${formatQty(amount)} ${sku.unit} from the Stock page`,
        });
        toast.success(`Added ${formatQty(amount)} ${sku.unit} to ${sku.name}`);
      } else {
        await setOnHand({ skuId: sku.skuId, quantity: 0, note: "Marked out of stock from the Stock page" });
        toast.success(`${sku.name} marked out of stock`);
      }
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={save} className="flex flex-col gap-4">
      {error ? <ErrorSurface error={error} action="set the stock" /> : null}
      <Checkbox label="In Stock" checked={inStock} onChange={(event) => setInStock(event.target.checked)} />
      <Field
        label={`Quantity to add (${sku.unit})`}
        name="quantity"
        id={`set-stock-quantity-${sku.skuCode}`}
        inputMode="decimal"
        required={inStock}
        disabled={!inStock}
        value={inStock ? quantity : "0"}
        onChange={(event) => setQuantity(event.target.value)}
        hint={inStock ? "Added to what is already on hand, in the SKU's stocking unit." : "On hand is set to 0: the SKU shows Out of stock."}
      />
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}

function Figure({ label, value, unit, tone }: { label: string; value: number | string; unit: string; tone?: string }) {
  return (
    <div className="rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-100">
      <Text className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</Text>
      <Text className={cn("mt-1 font-mono text-base font-semibold text-gray-900 tabular-nums", tone)}>
        {formatQty(value)} <span className="text-xs font-medium text-gray-500">{unit}</span>
      </Text>
    </div>
  );
}

/** A row's availability badge, with the separate flag beside it that opens the SKU's popup. */
export function AvailabilityWithFlag({ row }: { row: InventoryRow }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <AvailabilityBadge stock={row.stock} />
      <StockFlag row={row} />
    </div>
  );
}

function StockFlag({ row }: { row: InventoryRow }) {
  const maySetStock = useMaySetStock();
  const revalidator = useRevalidator();
  const [open, setOpen] = useState(false);
  const stock: FreeStockView = row.stock;
  const sku: Sku = { skuId: row.skuId, name: row.name, skuCode: row.skuCode, unit: stock.stockingUnitCode };
  const close = () => setOpen(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Add stock: ${row.name}`}
        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-primary/30 bg-primary-lighter/40 px-2.5 py-0.5 text-xs font-semibold text-primary transition hover:border-primary hover:bg-primary hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <PlusIcon aria-hidden className="size-3" />
        Add stock
      </button>
      <Popup open={open} onClose={close}>
        <div className="flex items-start justify-between gap-4 border-b border-muted p-6">
          <div className="min-w-0">
            <Title as="h3" className="truncate text-lg font-semibold text-gray-900">
              {row.name}
            </Title>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <Badge variant="flat" color="secondary" size="sm" rounded="md" className="font-mono">
                {row.skuCode}
              </Badge>
              <AvailabilityBadge stock={stock} />
            </div>
          </div>
          <Link to={inventoryPath(row.slug)} onClick={close} className="shrink-0 text-sm font-medium text-primary hover:underline">
            Stock position
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-3 p-6 pb-0">
          <Figure label="On hand" value={stock.qtyStockingUnit} unit={sku.unit} />
          <Figure label="Reserved" value={stock.reservedQty} unit={sku.unit} />
          <Figure label="Free" value={stock.freeStock} unit={sku.unit} tone={Number(stock.freeStock) <= 0 ? "text-red-dark" : undefined} />
        </div>
        <div className="p-6">
          {maySetStock ? (
            <SetStockForm
              sku={sku}
              onCancel={close}
              onSaved={() => {
                close();
                revalidator.revalidate();
              }}
            />
          ) : (
            <Text className="text-sm text-gray-500">Setting stock needs the permission to adjust it.</Text>
          )}
        </div>
      </Popup>
    </>
  );
}

/** The page header's one flag: how many SKUs are going out of stock, and the warning behind it. */
export function LowStockFlag({ view }: { view: LowStockView }) {
  const maySetStock = useMaySetStock();
  const revalidator = useRevalidator();
  const [open, setOpen] = useState(false);
  const [setting, setSetting] = useState<Sku | null>(null);
  const [enabled, setEnabled] = useState(view.enabled);
  const [threshold, setThreshold] = useState(String(view.thresholdKg));
  const [saving, setSaving] = useState(false);
  const count = view.items.length;

  const close = () => {
    setOpen(false);
    setSetting(null);
    setEnabled(view.enabled);
    setThreshold(String(view.thresholdKg));
  };

  async function saveWarning(event: FormEvent) {
    event.preventDefault();
    const thresholdKg = Number(threshold.trim());
    if (threshold.trim() === "" || !Number.isFinite(thresholdKg) || thresholdKg <= 0) {
      toast.error("Enter a threshold above zero kilograms");
      return;
    }
    setSaving(true);
    try {
      await setStockWarningSettings({ enabled, thresholdKg });
      toast.success(enabled ? "Out-of-stock warning on" : "Out-of-stock warning off");
      revalidator.revalidate();
    } catch {
      toast.error("Could not save the out-of-stock warning");
    } finally {
      setSaving(false);
    }
  }

  const label = !view.enabled
    ? "Out-of-stock warning off"
    : count === 0
      ? "Nothing going out of stock"
      : `${count} going out of stock`;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-full transition hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <Badge
          variant="flat"
          color={!view.enabled ? "secondary" : count === 0 ? "success" : "warning"}
          rounded="pill"
          className="gap-1.5 tabular-nums"
        >
          <WarningIcon aria-hidden className="size-3.5" />
          {label}
        </Badge>
      </button>

      <Popup open={open} onClose={close}>
        <div className="flex items-start gap-4 border-b border-muted p-6">
          <span className="grid size-11 shrink-0 place-items-center rounded-full bg-orange-lighter text-orange-dark">
            <WarningIcon aria-hidden className="size-6" />
          </span>
          <div className="min-w-0">
            <Title as="h3" className="text-lg font-semibold text-gray-900">
              {setting ? setting.name : "Going out of stock"}
            </Title>
            <Text className="mt-1 text-sm text-gray-500">
              {setting
                ? setting.skuCode
                : view.enabled
                  ? `SKUs whose free stock is at or below ${formatQty(view.thresholdKg)} kg, converted to each SKU's unit.`
                  : "The warning is off. Turn it on to flag SKUs approaching out of stock."}
            </Text>
          </div>
        </div>

        {setting ? (
          <div className="p-6">
            <button
              type="button"
              onClick={() => setSetting(null)}
              className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900"
            >
              <BackIcon aria-hidden className="size-3.5" /> Back to the list
            </button>
            <SetStockForm
              sku={setting}
              onCancel={() => setSetting(null)}
              onSaved={() => {
                setSetting(null);
                revalidator.revalidate();
              }}
            />
          </div>
        ) : (
          <>
            {maySetStock ? (
              <form onSubmit={saveWarning} className="flex flex-col gap-3 border-b border-muted bg-gray-50/60 px-6 py-4 dark:bg-gray-100/40">
                <Switch
                  label="Show Out-of-Stock Warning"
                  checked={enabled}
                  onChange={(event) => setEnabled(event.target.checked)}
                />
                <div className="flex items-end gap-3">
                  <Field
                    label="Warn at or below (kg)"
                    name="thresholdKg"
                    id="low-stock-threshold"
                    inputMode="decimal"
                    value={threshold}
                    onChange={(event) => setThreshold(event.target.value)}
                    disabled={!enabled}
                    wrapperClassName="flex-1"
                  />
                  <Button type="submit" variant="outline" disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              </form>
            ) : null}

            {view.enabled && count > 0 ? (
              <ul className="max-h-80 divide-y divide-dashed divide-muted overflow-y-auto px-6">
                {view.items.map((item) => (
                  <li key={item.skuId} className="flex items-center justify-between gap-4 py-3">
                    <Link to={inventoryPath(item.skuId)} onClick={close} className="min-w-0 hover:text-primary">
                      <span className="block truncate font-medium text-gray-900">{item.skuName ?? item.skuCode ?? "Unnamed SKU"}</span>
                      <span className="block font-mono text-xs text-orange-dark">
                        {formatQty(item.freeStock)} {item.stockingUnitCode} free
                      </span>
                    </Link>
                    {maySetStock ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setSetting({
                            skuId: item.skuId,
                            name: item.skuName ?? item.skuCode ?? "Unnamed SKU",
                            skuCode: item.skuCode ?? "",
                            unit: item.stockingUnitCode,
                          })
                        }
                      >
                        <PlusIcon aria-hidden className="me-1 size-3.5" />
                        Add stock
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : view.enabled ? (
              <Text className="px-6 py-8 text-center text-sm text-gray-500">No SKU is at or below the threshold right now.</Text>
            ) : null}

            <div className="flex justify-end border-t border-muted p-6">
              <Button variant="outline" onClick={close}>
                Close
              </Button>
            </div>
          </>
        )}
      </Popup>
    </>
  );
}
